import { Globe2, Link2 } from "lucide-react";
import { JobPostVisibility } from "@api/shared-runtime/enums";
import { cn } from "@/lib/utils";
import type { RouterOutputs } from "@/trpc";

type JobPostBadgeDetails = Pick<
  RouterOutputs["jobPost"]["orgList"]["jobs"][number],
  "open" | "publishedAt" | "reviewedAt" | "visibility"
>;

const JOB_POST_BADGE_CLASS_NAME =
  "inline-flex min-h-7 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 py-1 text-xs font-medium leading-4";

function getJobPostStatus(job: JobPostBadgeDetails) {
  if (!job.open) {
    return {
      label: "Closed",
      className: "border-slate-200 bg-slate-100 text-slate-600",
      dotClassName: "bg-slate-500",
    };
  }
  if (job.publishedAt && !job.reviewedAt) {
    return {
      label: "Pending Approval",
      className: "border-amber-200 bg-amber-50 text-amber-800",
      dotClassName: "bg-amber-500",
    };
  }
  if (job.publishedAt && job.reviewedAt) {
    return {
      label: "Published",
      className: "border-primary/15 bg-primary/5 text-primary",
      dotClassName: "bg-primary",
    };
  }
  return {
    label: "Draft",
    className: "border-slate-200 bg-slate-50 text-slate-600",
    dotClassName: "bg-white ring-1 ring-inset ring-slate-400",
  };
}

export function JobPostBadges({ job }: { job: JobPostBadgeDetails }) {
  const status = getJobPostStatus(job);
  const isLinkOnly = job.visibility === JobPostVisibility.DirectLink;
  const VisibilityIcon = isLinkOnly ? Link2 : Globe2;

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <span className={cn(JOB_POST_BADGE_CLASS_NAME, status.className)}>
        <span
          aria-hidden="true"
          className={cn(
            "h-1.5 w-1.5 shrink-0 rounded-full",
            status.dotClassName,
          )}
        />
        {status.label}
      </span>
      <span
        className={cn(
          JOB_POST_BADGE_CLASS_NAME,
          "border-slate-200 bg-slate-50 text-slate-600",
        )}
      >
        <VisibilityIcon
          aria-hidden="true"
          className="h-3.5 w-3.5 shrink-0 text-slate-500"
        />
        {isLinkOnly ? "Link only" : "Public"}
      </span>
    </div>
  );
}
